// Web Audio helpers for /piano-drills — same lightweight posture as
// scaleCountryAudio.js (lazy shared AudioContext, no libraries), but with
// absolute-time scheduling so the transport can run a lookahead clock, plus a
// metronome click.

let audioCtx;

const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
};

export const audioSupported = () =>
  typeof window !== "undefined" && Boolean(window.AudioContext || window.webkitAudioContext);

// Call from a user gesture (play button, key press) so playback is allowed.
export const ensureAudio = () => Boolean(getCtx());

// Current audio-clock time in seconds; the transport schedules against this.
export const audioNow = () => {
  const ctx = getCtx();
  return ctx ? ctx.currentTime : 0;
};

export const midiToFreq = (midi) => 440 * 2 ** ((midi - 69) / 12);

// One piano-ish tone at an absolute audio-clock time: triangle oscillator,
// fast exponential attack, exponential decay over the note's duration.
export const playToneAt = (midi, atSec, { durMs = 450, gain = 0.14, type = "triangle" } = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = Math.max(atSec, ctx.currentTime);
  const dur = durMs / 1000;
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = midiToFreq(midi);
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
};

// Immediate tone for live input (on-screen keys / MIDI keyboard echo).
export const playTone = (midi, opts = {}) => {
  const ctx = getCtx();
  if (!ctx) return;
  playToneAt(midi, ctx.currentTime, opts);
};

// Short metronome click; the accented click (bar start) is higher and louder.
export const playClickAt = (atSec, accent = false) => {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = Math.max(atSec, ctx.currentTime);
  const g = ctx.createGain();
  g.connect(ctx.destination);
  g.gain.setValueAtTime(accent ? 0.12 : 0.07, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
  const osc = ctx.createOscillator();
  osc.type = "square";
  osc.frequency.value = accent ? 2100 : 1500;
  osc.connect(g);
  osc.start(t0);
  osc.stop(t0 + 0.08);
};
